// AICA Collapsible Component
// Expandable/collapsible sections for better information hierarchy

import * as React from 'react';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const Collapsible = CollapsiblePrimitive.Root;

const CollapsibleTrigger = CollapsiblePrimitive.Trigger;

const CollapsibleContent = CollapsiblePrimitive.Content;

export { Collapsible, CollapsibleTrigger, CollapsibleContent };

export interface CollapsibleSectionProps {
  /**
   * Section title
   */
  title: React.ReactNode;
  /**
   * Section content
   */
  children: React.ReactNode;
  /**
   * Default open state
   */
  defaultOpen?: boolean;
  /**
   * Controlled open state
   */
  open?: boolean;
  /**
   * Callback when open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Optional icon to show instead of chevron
   */
  icon?: React.ReactNode;
  /**
   * Optional badge/count to show
   */
  badge?: React.ReactNode;
  /**
   * Additional className for root
   */
  className?: string;
  /**
   * Additional className for content
   */
  contentClassName?: string;
  /**
   * Variant style
   */
  variant?: 'default' | 'bordered' | 'elevated';
}

/**
 * Collapsible section component
 * Expandable/collapsible container for organizing content
 *
 * @example
 * ```tsx
 * <CollapsibleSection title="Advanced Settings" defaultOpen={false}>
 *   <div className="space-y-4">
 *     {/* Form fields or content *\/}
 *   </div>
 * </CollapsibleSection>
 * ```
 */
export function CollapsibleSection({
  title,
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  icon,
  badge,
  className,
  contentClassName,
  variant = 'default',
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const handleOpenChange = (newOpen: boolean) => {
    if (open === undefined) {
      setIsOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  const currentOpen = open !== undefined ? open : isOpen;

  return (
    <Collapsible
      open={currentOpen}
      onOpenChange={handleOpenChange}
      className={cn('group', className)}
    >
      <CollapsibleTrigger
        className={cn(
          'flex w-full items-center justify-between gap-4 text-left transition-all duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          variant === 'default' && 'rounded-button p-4 hover:bg-bg-elevated',
          variant === 'bordered' &&
            'rounded-lg border border-border bg-bg p-4 hover:bg-bg-elevated',
          variant === 'elevated' &&
            'rounded-lg border border-border bg-bg-elevated p-4 shadow-sm hover:shadow-md'
        )}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {icon && <div className="flex-shrink-0 text-fg-muted">{icon}</div>}
          <h3 className="font-semibold text-fg text-base">{title}</h3>
          {badge && <div className="flex-shrink-0">{badge}</div>}
        </div>
        <ChevronDown
          className={cn(
            'h-5 w-5 flex-shrink-0 text-fg-muted transition-transform duration-150',
            currentOpen && 'rotate-180'
          )}
          aria-hidden="true"
        />
      </CollapsibleTrigger>

      <CollapsibleContent
        className={cn(
          'overflow-hidden transition-all duration-150',
          'data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down'
        )}
      >
        <div
          className={cn('pt-4 pb-2', variant !== 'default' && 'px-4', contentClassName)}
        >
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Simple collapsible without styling (for custom designs)
 */
export function CollapsibleSimple({
  title,
  children,
  defaultOpen = true,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 font-medium text-fg hover:text-primary transition-all duration-150">
        <ChevronDown
          className={cn(
            'h-4 w-4 transition-transform duration-150',
            isOpen && 'rotate-180'
          )}
        />
        {title}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">{children}</CollapsibleContent>
    </Collapsible>
  );
}
